import React, { useEffect, useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  FlatList,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { volver } from '../utils/volver';
import AccionesDeChat, { AvisoDeBloqueo } from '../components/AccionesDeChat';
import PantallaSegura from '../components/PantallaSegura';

/**
 * Chat in-order entre customer y repartidor.
 *
 * Reglas Firestore: /food_orders/{id}/messages
 *   - read: customerId / driverId del order parent
 *   - create: senderId == auth.uid && (es customer o driver)
 */
export default function OrderChatScreen() {
  const router = useRouter();
  const { orderId } = useLocalSearchParams<{ orderId: string }>();
  const [chatBloqueado, setChatBloqueado] = useState(false);
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);
  const userName = useAppStore((s) => s.userName);

  const [order, setOrder] = useState<any | null>(null);
  /**
   * Si el OTRO me bloqueó a mí, la caja de texto también tiene que cerrarse:
   * si no, escribo, el servidor lo deniega por regla y para mí la app se
   * rompió sin decir por qué. El servidor responde sí o no, nunca quién.
   */
  useEffect(() => {
    let vivo = true;
    const yo = useAppStore.getState().userFirebaseUid;
    const otro = order
      ? (order.customerId === yo ? order.driverId : order.customerId)
      : null;
    if (!yo || !otro) return;
    firestoreService
      .hayBloqueoEntre(String(yo), String(otro))
      .then((b) => { if (vivo) setChatBloqueado(!!b); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [order]);
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const listRef = useRef<FlatList>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    const unsub = firestoreService.listenToFoodOrderById(orderId, (o) => {
      setOrder(o);
      setLoading(false);
    });
    return () => unsub();
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    const unsub = firestoreService.listenToFoodOrderMessages(orderId, (msgs) => {
      setMessages(msgs);
      // auto-scroll
      setTimeout(() => listRef.current?.scrollToEnd({ animated: true }), 50);
    });
    return () => unsub();
  }, [orderId]);

  const handleSend = async () => {
    if (!text.trim() || !userFirebaseUid || !order || !orderId) return;
    // El recipiente es la contraparte
    const recipientId = order.customerId === userFirebaseUid ? order.driverId : order.customerId;
    if (!recipientId) {
      // Aún no hay repartidor asignado
      return;
    }
    setSending(true);
    try {
      await firestoreService.sendFoodOrderMessage(
        orderId,
        userFirebaseUid,
        recipientId,
        text
      );
      setText('');
    } catch (e) {
      console.warn('No se pudo enviar mensaje:', e);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <PantallaSegura style={[styles.safe, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={Colors.primary} />
      </PantallaSegura>
    );
  }

  if (!order) {
    return (
      <PantallaSegura style={[styles.safe, { justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <Ionicons name="alert-circle-outline" size={56} color={Colors.border} />
        <Text style={{ marginTop: 10, color: Colors.textSecondary }}>Pedido no encontrado.</Text>
      </PantallaSegura>
    );
  }

  const isCustomer = order.customerId === userFirebaseUid;
  const otherLabel = isCustomer ? 'Repartidor' : (order.customerName ?? 'Cliente');
  // El otro es el que no soy yo. Mismo criterio que usa `handleSend` para el
  // `recipientId`, para que bloquear y escribir hablen de la misma persona.
  const contraparteUid = isCustomer ? (order.driverId ?? null) : (order.customerId ?? null);
  const hasCounterparty = isCustomer ? !!order.driverId : !!order.customerId;

  return (
    <PantallaSegura style={styles.safe}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => volver(router)} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={Colors.text} />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={styles.headerTitle}>{otherLabel}</Text>
          <Text style={styles.headerSub}>{order.restaurantName}</Text>
        </View>
        {/* Bloquear / reportar (política de contenido de Google Play). */}
        <AccionesDeChat
          otroUid={contraparteUid}
          otroNombre={otherLabel}
          contexto="PEDIDO"
          referenciaId={orderId}
          onCambioDeBloqueo={setChatBloqueado}
        />
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
      >
        <FlatList
          ref={listRef}
          data={messages}
          keyExtractor={(m) => m.id}
          contentContainerStyle={{ padding: 14 }}
          renderItem={({ item }) => {
            const mine = item.senderId === userFirebaseUid;
            return (
              <View
                style={[
                  styles.bubble,
                  mine ? styles.bubbleMine : styles.bubbleTheirs,
                  { alignSelf: mine ? 'flex-end' : 'flex-start' },
                ]}
              >
                <Text style={mine ? styles.textMine : styles.textTheirs}>{item.text}</Text>
              </View>
            );
          }}
          ListEmptyComponent={
            <View style={{ alignItems: 'center', paddingTop: 40 }}>
              <Ionicons name="chatbubbles-outline" size={48} color={Colors.border} />
              <Text style={{ color: Colors.textSecondary, marginTop: 8 }}>
                {hasCounterparty
                  ? 'Comienza la conversación.'
                  : 'Esperando que un repartidor tome tu pedido.'}
              </Text>
            </View>
          }
        />

        <AvisoDeBloqueo bloqueado={chatBloqueado} />

        {!chatBloqueado && (
        <View style={styles.composer}>
          <TextInput
            style={styles.input}
            placeholder={hasCounterparty ? 'Escribe un mensaje…' : 'Sin repartidor aún'}
            value={text}
            onChangeText={setText}
            editable={hasCounterparty}
            maxLength={500}
          />
          <TouchableOpacity
            onPress={handleSend}
            disabled={!text.trim() || sending || !hasCounterparty}
            style={[
              styles.sendBtn,
              (!text.trim() || !hasCounterparty) && { opacity: 0.4 },
            ]}
          >
            {sending ? (
              <ActivityIndicator color="#FFF" />
            ) : (
              <Ionicons name="send" size={20} color="#FFF" />
            )}
          </TouchableOpacity>
        </View>
        )}
      </KeyboardAvoidingView>
    </PantallaSegura>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#FFF' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  backBtn: { padding: 5, marginRight: 8 },
  headerTitle: { fontSize: 16, fontWeight: '700', color: Colors.text },
  headerSub: { fontSize: 12, color: Colors.textSecondary, marginTop: 2 },
  bubble: { padding: 10, borderRadius: 14, marginVertical: 4, maxWidth: '78%' },
  bubbleMine: { backgroundColor: Colors.primary },
  bubbleTheirs: { backgroundColor: '#F2F2F2' },
  textMine: { color: '#FFF', fontSize: 14 },
  textTheirs: { color: Colors.text, fontSize: 14 },
  composer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: '#F0F0F0',
  },
  input: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 22,
    fontSize: 14,
    color: Colors.text,
  },
  sendBtn: {
    backgroundColor: Colors.primary,
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
});
